import { CameraOnlineStatus } from '@app/main/components/videos/models/camera-online-status';
import { IVehicleOnlineStatus } from '@app/main/components/videos/models/IVehicleOnlineStatus';
import { DateService, DateUnit } from '@app/services/date.service';
import { IDriverTachoHours } from '@app/services/tacho.service';
import { AccountFeatures } from '@enums/account/AccountFeature';
import { IAccountUnitMappingExtended } from '@interfaces/IAccountUnitMappingExtended';
import { IKeyValuePair } from '@interfaces/IKeyValuePair';
import { IPeripheral, IUnit } from '@interfaces/IPeripheral';
import { IPlot } from '@interfaces/IPlot';
import { IBasicProductModel } from '@interfaces/IProductUiViewModel';
import { ISensorData } from '@interfaces/ISensorData';
import { IVehicle } from '@interfaces/IVehicle';
import { IVehiclePeripheralMapping, IVehicleUnitMapping } from '@interfaces/IVehiclePeripheralMapping';
import { IVehicleUnitMappingExtended } from '@interfaces/IVehicleUnitMappingExtended';
import { EnumService } from '@services/enum.service';
import { ProductService } from '@services/product.service';
import { VeriDate } from '@services/veriDate.service';
import * as moment from "moment";
import { formatBytes } from '../../../../filters/formatBytes.filter';
import { ProductVendor } from '../../../components/videos/enums/CameraProductType';
import { ISerialSearchResponse } from '../_models/ISerialSearchResponse';


export enum UnitConfigAndFirmwareStatus {
    Unknown,
    Completed,
    Pending,
    OtaSent,
    InProgress,
    Partial,
    Timeout,
    Error,
    Cancelled,
    Created,
    Discovered,
}

export interface IConfigMappings {
    Identifier: string;
    Description: string;
    IsActive: boolean;
    Id: number;
    Serial: string;
    UnitConfigId: number;
    StartUtc: string;
    EndUtc: string | null;
    UnitConfigStatus: number;
}

export interface IFirmwareMappings {
    Id: number;
    Serial: string;
    StartUtc: string;
    EndUtc: string | null;
    UnitFirmwareId: number;
    UnitFirmwareStatus: number;
    Identifier: string;
    Description: string;
    IsActive: boolean;
}
export interface ITele2SimSessionDetails {
    iccid: string;
    ipAddress: string;

    dateSessionStarted: string;
    dateSessionEnded: string;
}

export interface ITele2Sim extends ITele2BasicSim {
    imsi: string;
    msisdn: string;
    imei: string;
}

export interface ITele2BasicSim {
    iccid: string;
    status: string;
    ratePlan: string;
    communicationPlan: string;
    errorMessage: string;
}

export interface ITele2SimUsage extends ITele2Sim {
    ctdDataUsage: number;

    dataUsageFormatted: string;
    simUsage: number;
    voiceUsage: number;
    sessionCount: number;
    overageLimitReached: boolean;
    overageLimitOverride: string;
}

export interface ISimStatusInfo {
    Sim: /*Sim*/any;

    SessionDetails: ITele2SimSessionDetails;

    Usage: ITele2SimUsage;
}

export class FoundationPeripheralService {

    static $inject = ["$q", "$http", "veriDate", "productService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly veriDate: VeriDate,
        private readonly productService: ProductService) { }

    // any peripherals not matching a feature / has issue, dump in peripherals array

    getAllPeripherals(invalidateCache = false): angular.IPromise<IPeripheral[]> {
        return this
            .$http({
                url: `/api/foundation/peripherals/`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPeripheral[]>) => {
                return this.getProductsAndMap(response.data);
            });
    }

    getPeripherals(vehicleId: number, invalidateCache = false): angular.IPromise<IPeripheral[]> {
        return this
            .$http({
                url: `/api/foundation/peripherals/${vehicleId}`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache, plotOption: "WithFeatures" }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPeripheral[]>) => {
                return this.getProductsAndMap(response.data);
            });
    }

    getPeripheralMappings(peripheralSerial: string, invalidateCache = false): angular.IPromise<IVehiclePeripheralMapping> {
        return this
            .$http({
                url: `/api/foundation/peripheral/mapping/current/${peripheralSerial}`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehiclePeripheralMapping>) => {
                return response.data;
            });
    }

    getSimInfo(serial: string, invalidateCache = false): angular.IPromise<ISimStatusInfo> {
        return this
            .$http({
                url: `/api/foundation/units/${serial}/simstatus`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<ISimStatusInfo>) => {

                if (response.data.Usage) {
                    response.data.Usage.dataUsageFormatted = formatBytes()(response.data.Usage.ctdDataUsage);
                }

                return response.data;
            });
    }

    updateSim(serial: string, iccid: string, invalidateCache = false): angular.IPromise<number> {
        return this
            .$http({
                url: `/api/foundation/units/${serial}/updatesim`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache, iccid }
            })
            .then((response: angular.IHttpPromiseCallbackArg<number>) => {

                return response.data;
            });
    }

    getPlotsByImei(imei: string, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<IPlot[]> {

        return this
            .$http({
                url: `/api/foundation/units/${imei}/plots`,
                method: "GET",
                params: {
                    minsToLive: 1,
                    start: startTime.toISOString(),
                    end: endTime.toISOString(),
                    invalidateCache: invalidateCache
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlot[]>) => {
                return response.data;
            });
    }

    getExtendedPlotsByImei(imei: string, jobItemVehicleId?: number, invalidateCache = false): angular.IPromise<IPlot[]> {

        return this
            .$http({
                url: `/api/foundation/units/${imei}/extendedPlots`,
                method: "GET",
                params: {
                    minsToLive: 1,
                    jobItemVehicleId: jobItemVehicleId,
                    invalidateCache: invalidateCache
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlot[]>) => {
                return response.data;
            });
    }

    getLatestPlotsByImei(imei: string, amount: number = 10, invalidateCache = false): angular.IPromise<IPlot[]> {

        return this
            .$http({
                url: `/api/foundation/units/${imei}/latestplots`,
                method: "GET",
                params: {
                    minsToLive: 1,
                    amount: amount,
                    invalidateCache: invalidateCache
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IPlot[]>) => {
                return response.data;
            });
    }

    getSensorDataByImei(imei: string, startTime: moment.Moment, endTime: moment.Moment, invalidateCache = false): angular.IPromise<ISensorData[]> {

        return this
            .$http({
                url: `/api/foundation/units/${imei}/sensordata`,
                method: "GET",
                params: {
                    minsToLive: 5,
                    invalidateCache: invalidateCache,
                    start: startTime.toISOString(),
                    end: endTime.toISOString()
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<ISensorData[]>) => {
                return response.data;
            });
    }

    getUnitData(unitId: string, invalidateCache = false): angular.IPromise<ISerialSearchResponse> {

        return this
            .$http({
                url: `/api/foundation/units/${unitId}/unitData`,
                method: "GET",
                params: { minsToLive: 5, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<ISerialSearchResponse>) => {


                // This matches previous data architecture
                response.data.Unit.Product = response.data.Product;

                return response.data;
            });
    }

    getConfigMappings(serial: string, invalidateCache = false): angular.IPromise<IConfigMappings[]> {

        return this
            .$http({
                url: `/api/foundation/units/${serial}/configmappings`,
                method: "GET",
                params: { minsToLive: 5, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IConfigMappings[]>) => {
                return response.data;
            });
    }

    getFirmwareMappings(serial: string, invalidateCache = false): angular.IPromise<IFirmwareMappings[]> {

        return this
            .$http({
                url: `/api/foundation/units/${serial}/firmwaremappings`,
                method: "GET",
                params: { minsToLive: 5, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IFirmwareMappings[]>) => {
                return response.data;
            });
    }

    getUnitOrPeripheralBySerial(serial: string, invalidateCache = false): angular.IPromise<ISerialSearchResponse> {

        if (serial?.length < 6) {
            return this.$q.when(null);
        }

        return this
            .$http({
                url: `/api/foundation/units/${serial}`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<ISerialSearchResponse>) => {
                var searchResponse = response.data;

                var commonProperties: IUnit = searchResponse.Unit || searchResponse.Peripheral;

                if (commonProperties) {
                    searchResponse.Id = commonProperties.Id;
                    searchResponse.ProductId = commonProperties.ProductId;
                    searchResponse.Serial = commonProperties.Serial;
                    searchResponse.SimId = commonProperties.SimId;
                    searchResponse.Imei = commonProperties.Imei;

                    searchResponse.vendorString = ProductVendor[searchResponse.Product.GroupId];
                }

                return searchResponse;
            });
    }

    getAllPeripheralMappings(invalidateCache = false): angular.IPromise<IVehiclePeripheralMapping[]> {
        return this
            .$http({
                url: `/api/foundation/vehicles/peripheralmappings`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehiclePeripheralMapping[]>) => {

                return response.data;
            });
    }

    getUnitMappings(vehicleId: number, invalidateCache = false): angular.IPromise<IVehicleUnitMapping[]> {
        return this
            .$http({
                url: `/api/foundation/vehicles/${vehicleId}/unitmappings`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleUnitMapping[]>) => {

                return response.data;
            });
    }

    getVehicleMappings(serial: string, invalidateCache = false): angular.IPromise<IVehicleUnitMappingExtended[]> {
        return this
            .$http({
                url: `/api/foundation/units/${serial}/vehiclemappings`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleUnitMappingExtended[]>) => {

                return response.data;
            });
    }

    getAccountMappings(serial: string, invalidateCache = false): angular.IPromise<IAccountUnitMappingExtended[]> {
        return this
            .$http({
                url: `/api/foundation/units/${serial}/accountmappings`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IAccountUnitMappingExtended[]>) => {

                return response.data;
            });
    }

    getUnit(vehicleId: number, invalidateCache = false): angular.IPromise<IUnit> {
        return this
            .$http({
                url: `/api/foundation/vehicles/${vehicleId}/unit`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IUnit>) => {

                return response.data;
            });
    }

    getAllUnits(invalidateCache = false): angular.IPromise<IUnit[]> {
        return this
            .$http({
                url: `/api/foundation/units`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleUnitMapping[]>) => {
                return this.getProductsAndMap(response.data);
            });
    }

    getAllUnitMappings(invalidateCache = false): angular.IPromise<IVehicleUnitMapping[]> {
        return this
            .$http({
                url: `/api/foundation/vehicles/unitmappings`,
                method: "GET",
                params: { minsToLive: 60, invalidateCache: invalidateCache }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IVehicleUnitMapping[]>) => {

                return response.data;
            });
    }

    getTachoRecordsForPeripheral(peripheralString: string, start: Date, end: Date, invalidateCache = false): angular.IPromise<IDriverTachoHours[]> {
        return this
            .$http({
                url: `/api/foundation/peripherals/tacho/${peripheralString}`,
                method: "GET",
                params: {
                    minsToLive: 60,
                    start: start.toISOString(),
                    end: end.toISOString(),
                    invalidateCache: invalidateCache
                }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IDriverTachoHours[]>) => {
                return response.data;
            });
    }

    getSingleFeatureForPeripheral(peripheral: IPeripheral): AccountFeatures {

        if (!AccountFeatures[peripheral?.Product?.Features]) {
            return AccountFeatures.Default;
        }

        const features = this.findFeaturesFromFeaturesEnum(peripheral.Product.Features);

        if (features.indexOf(AccountFeatures.Camera) > -1) {
            return AccountFeatures.Camera;
        }

        return (features?.length) ? features[0] : null;
    }

    findFeaturesFromFeaturesEnum(value: number): AccountFeatures[] {
        const features: IKeyValuePair<typeof AccountFeatures>[] = EnumService.enumToArray("AccountFeatures", AccountFeatures);
        const includedFeatures: AccountFeatures[] = [];

        if (!features.length) {
            return includedFeatures;
        }

        features.sort((feature1: IKeyValuePair<typeof AccountFeatures>, feature2: IKeyValuePair<typeof AccountFeatures>) => {
            return (+feature2.value) - (+feature1.value)
        });

        features.forEach((feature, index) => {
            const enumValue: number = +feature.value;

            if (value == enumValue || value / enumValue >= 1) {
                includedFeatures.push(enumValue);
                value -= enumValue;
            }
        });

        return includedFeatures;
    }

    setMetaDataOnPeripheral(peripheral: IPeripheral, feature?: AccountFeatures) {
        if (!peripheral) {
            console.error("No peripheral specified");
            return;
        }

        peripheral.primaryFeature = this.getSingleFeatureForPeripheral(peripheral);
        peripheral.productVendorString = this.productService.getProductVendorString(peripheral.Product);


        peripheral.name = `${peripheral.productVendorString ? peripheral.productVendorString : ''} ${peripheral.Product?.Description}`;
        peripheral.icon = this.getIconForPeripheral(peripheral);
    }

    createInstalledPeripheralList(peripherals: IPeripheral[]): { installed: IPeripheral[], notInstalled: IPeripheral[] } {
        const featuresEnumArray: IKeyValuePair<typeof AccountFeatures>[] = EnumService.enumToArray("AccountFeatures", AccountFeatures);

        var peripheralsCurrentlyInstalled: IPeripheral[] = [];
        var peripheralsNotInstalled: IPeripheral[] = [];

        featuresEnumArray.forEach(featureEnum => {
            const feature: AccountFeatures = +featureEnum.value;
            const peripheral = this.findInstalledPeripheralForFeature(feature, peripherals, peripheralsCurrentlyInstalled);

            if (peripheral) {
                this.setMetaDataOnPeripheral(peripheral, feature);
                peripheralsCurrentlyInstalled.push(peripheral);
            }
            else {
                peripheralsNotInstalled.push(peripheral);
            }
        });

        return {
            installed: peripheralsCurrentlyInstalled,
            notInstalled: peripheralsNotInstalled
        };
    }

    getProductsAndMap(units: IUnit[]): angular.IPromise<IUnit[]> {
        return this.productService.getProductUiViewModels().then(products => {
            this.mapProductsToUnits(units, products);

            return units;
        });
    }

    mapProductsToUnits(units: IUnit[], products: IBasicProductModel[]) {
        units.forEach(unit => {
            unit.Product = products.find(product => product.ProductId === unit.ProductId);

            this.setMetaDataOnPeripheral(unit);
        });
    }

    getPeripheralsAndMapToVehicles(vehicles: IVehicle[], invalidateCache: boolean = false): angular.IPromise<void> {
        const peripheralPromise = this.getAllPeripherals(invalidateCache);
        const peripheralMappingPromise = this.getAllPeripheralMappings(invalidateCache);

        return this.$q.all([peripheralPromise, peripheralMappingPromise]).then(([peripherals, peripheralMappings]) => {

            vehicles.forEach(vehicle => {
                const now = new Date();
                const mappings = peripheralMappings.filter(mapping => mapping.VehicleId === vehicle.Id);
                const relevantPeriperals: IPeripheral[] = [];

                mappings.forEach(mapping => relevantPeriperals.push(...peripherals.filter(peripheral => {
                    if (this.veriDate.date1IsBeforeDate2(mapping.EndUtc, now)) {
                        return false;
                    }

                    return peripheral.Serial === mapping.Serial;
                })));

                vehicle.peripherals = relevantPeriperals;
            });
        });
    }

    mapPeripheralsAndVehicles(
        vehicles: IVehicle[], 
        peripherals: IPeripheral[], 
        peripheralMappings: IVehiclePeripheralMapping[],
        onlineStatuses: CameraOnlineStatus[]
    ): void {

        const mappingsByVehicleId: { [vehicleId: number]: IVehiclePeripheralMapping[] } = {};
        peripheralMappings.forEach(mapping => {
            if (!mappingsByVehicleId[mapping.VehicleId]) {
                mappingsByVehicleId[mapping.VehicleId] = [];
            }
            mappingsByVehicleId[mapping.VehicleId].push(mapping);
        });

        const now = new Date();
        vehicles.forEach(vehicle => {
            const mappings = mappingsByVehicleId[vehicle.Id] || [];
            const relevantPeriperals: IPeripheral[] = [];

            mappings.forEach(mapping => relevantPeriperals.push(...peripherals.filter(peripheral => {
                
                if (peripheral.Serial !== mapping.Serial) {
                    return false;
                }

                return !this.veriDate.date1IsBeforeDate2(mapping.EndUtc, now);
            })));

            vehicle.peripherals = relevantPeriperals;
            vehicle.onlineStatus = this.findAndGetOnlineStatus(vehicle, onlineStatuses)
            vehicle.hasCamera = relevantPeriperals.some(peripheral => peripheral.Product?.IsCameraProduct);
        });
    }

    getOnlineStatus(vehicle: IVehicle, onlineStatus: CameraOnlineStatus) : IVehicleOnlineStatus {
        if (!onlineStatus) {
            return {
                lastOnlineTime: null,
                online: false,
                hasStreaming: false,
                sortingTime: null
            }
        }

        const lastOnlineTime = new Date(onlineStatus.LastOnlineTime);
        
        const hasStreaming = vehicle.peripherals.some(p => 
            p.Serial === onlineStatus.Serial && 
            p.Product?.ProductMetadata?.CameraFeatures?.LiveStream
        );

        return {
            lastOnlineTime,
            online: onlineStatus.Online,
            hasStreaming,
            sortingTime: this.veriDate.createMoment(lastOnlineTime).add(1, 's').toDate()
        } 
    }

    private findAndGetOnlineStatus(vehicle: IVehicle, onlineStatuses: CameraOnlineStatus[]) : IVehicleOnlineStatus {
        const onlineStatus = onlineStatuses.find(x => x.VehicleId === vehicle.Id);

        if (!onlineStatus) {
            return {
                lastOnlineTime: null,
                online: false,
                hasStreaming: false,
                sortingTime: null
            }
        }

        return this.getOnlineStatus(vehicle, onlineStatus);
    }

    isAnyPeripheralCamera(peripherals: IPeripheral[]): boolean {
        return peripherals.some(p => this.getCameraProductVendors().includes(p.Product?.GroupId));
    }

    // ---

    private getCameraProductVendors(): ProductVendor[] {
        return [
            ProductVendor.IntelligentTelematics,
            ProductVendor.AAMP,
            ProductVendor.Streamax,
            ProductVendor.VisionTrack,
            ProductVendor.RTL
        ];
    }

    private findInstalledPeripheralForFeature(featureIndex: number, peripherals, peripheralsCurrentlyInstalled): IPeripheral {
        if (!peripherals?.length) {
            return null;
        }

        let chosenPeripheral: IPeripheral;

        peripherals.forEach(peripheral => {
            const peripheralFeatures = this.findFeaturesFromFeaturesEnum(peripheral.Product.Features);

            if (!peripheralFeatures?.length || !!chosenPeripheral) {
                return;
            }

            for (var i = 0; i < peripheralFeatures.length; i++) {
                const feature = peripheralFeatures[i];
                const alreadyAddedPeripheral = peripheralsCurrentlyInstalled.find(installedPeripheral => installedPeripheral.Serial === peripheral.Serial);

                if (feature === featureIndex && !alreadyAddedPeripheral) {
                    chosenPeripheral = peripheral;

                    break;
                }
            }
        });

        return chosenPeripheral;
    }

    getIconForPeripheral(peripheral: IPeripheral): string {
        if (!peripheral) {
            return;
        }

        return this.getIconForFeature(peripheral.primaryFeature);
    }

    getIconForFeature(feature: AccountFeatures) {
        switch (feature) {
            case AccountFeatures.Camera:
                return `videocam`;

            case AccountFeatures.GPS:
                return `near_me`;

            default:
                return `token`;
        }
    }
}
